import { pushModal } from '@/components/modals'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useGlobalStore } from '@/hooks/use-global-store'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import type { UserProject } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'
import { format } from 'date-fns'
import { Layout, LockIcon, PaletteIcon, Trash2, Users } from 'lucide-react'
import { Logo } from '@/components/logo'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import useSWRInfinite, { type SWRInfiniteKeyedMutator } from 'swr/infinite'
import { LoadMoreBtn } from '../load-more-btn'

const FILTERS = [
  {
    id: 'all',
    label: 'All Projects',
    color: 'from-gray-500 to-gray-600',
    bgColor: 'bg-gray-500/10',
    hoverColor: 'hover:bg-gray-500/20',
  },
  {
    id: 'featured',
    label: 'Featured',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-500/10',
    hoverColor: 'hover:bg-purple-500/20',
  },
]

export const LatestProjectList = ({
  transparent = false,
  showTitle = true,
}: {
  transparent?: boolean
  showTitle?: boolean
}) => {
  const { data: user } = useCurrentUser()
  const limit = 8 // Reduced from 20 to 8 for faster initial load
  const [filter, setFilter] = useState('all')
  const t = useTranslations('projects')

  // Pagination with SWR Infinite
  const { data, error, size, setSize, isLoading, isValidating } =
    useSWRInfinite(
      (pageIndex) => {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: (pageIndex * limit).toString(),
          ...(filter === 'featured' && { is_featured: 'true' }),
        })
        return `/api/projects/v2?${params}`
      },
      fetcher,
      {
        revalidateFirstPage: false,
        parallel: false, // Disable parallel requests to reduce server load
        initialSize: 1, // Start with only 1 page
        revalidateOnFocus: false, // Don't revalidate on window focus
        revalidateOnReconnect: false, // Don't revalidate on reconnect
        dedupingInterval: 60000, // Dedupe requests for 1 minute
        keepPreviousData: true, // Keep previous data while loading new pages
      },
    )

  const projects = data ? data.flat() : []
  const isReachingEnd = data && data[data.length - 1]?.length < limit

  // Show cards as soon as we have projects
  const hasProjects = projects.length > 0

  if (error) return <p className="text-red-500">{t('failed')}</p>

  // Show loading overlay when initially loading
  const showInitialLoading = isLoading && projects.length === 0

  return (
    <div
      className={cn(
        'rounded-2xl',
        !transparent &&
          'bg-gradient-to-br from-white via-white to-gray-50 border border-gray-200/50 shadow-xl shadow-gray-200/50 backdrop-blur-xl',
      )}
    >
      {showTitle && (
        <div className="mb-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <PaletteIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-2xl font-black text-gray-900">
              {t('latest_creations.title')}
            </h2>
          </div>
          <p className="text-gray-500 ml-[44px]">
            {t('latest_creations.subtitle')}
          </p>
        </div>
      )}

      <div className="flex gap-2 ml-[16px] mb-8 justify-center items-center">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setFilter(item.id)}
            className={cn(
              'px-3 md:px-4 py-2 rounded-lg text-sm transition-all whitespace-nowrap flex-shrink-0 hover:bg-black/40',
              filter === item.id
                ? 'font-medium bg-black/40 text-white border border-white/10'
                : 'bg-black/30 text-white/60',
            )}
          >
            {t(`filters.${item.id}`)}
          </button>
        ))}
      </div>

      <div className="relative">
        {showInitialLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/20 backdrop-blur-sm rounded-2xl">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
              <span className="text-white/80 text-sm font-medium">
                Loading projects...
              </span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1800px]:grid-cols-5 gap-4">
          {/* Show actual project cards as they load */}
          {projects.map((project) => (
            <ProjectCard
              key={project._id.toString()}
              project={project}
              isOwner={project.user_id === user?._id.toString()}
              useHref
              onClickLink={(e) => {
                if (!user) {
                  e.preventDefault()
                  pushModal('LoginModal', { redirectUrl: '/' })
                  return
                }
                // add mixpanel event
                sendToBothAnalytics(
                  MIXPANEL_CLIENT_EVENT_NAMES.VIEW_LATEST_PROJECTS,
                  {
                    project_id: project._id.toString(),
                  },
                  user._id.toString(),
                )
              }}
              transparent={transparent}
            />
          ))}

          {/* Show individual skeleton cards while loading more data */}
          {isValidating && projects.length > 0 && (
            <>
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={`loading-skeleton-${index}`}
                  className="animate-pulse"
                >
                  <div className="bg-gray-700/20 rounded-lg h-48 mb-3 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-600/0 via-gray-500/10 to-gray-600/0 -translate-x-full animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-600/30 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-600/20 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Show skeleton cards when loading and no projects yet */}
          {isLoading && projects.length === 0 && !hasProjects && (
            <>
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={`skeleton-${index}`} className="animate-pulse">
                  <div className="bg-gray-700/30 rounded-lg h-48 mb-3 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-600/0 via-gray-500/20 to-gray-600/0 -translate-x-full animate-[shimmer_1.5s_infinite]" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-600/40 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-600/30 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Show loading indicator when loading additional pages */}
      {isValidating && projects.length > 0 && (
        <div className="flex justify-center mt-6 relative">
          <div className="flex items-center gap-2 text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
            <span className="text-sm">Loading more projects...</span>
          </div>
        </div>
      )}

      <div className="flex justify-center mt-6 relative">
        {!isReachingEnd && (
          <LoadMoreBtn
            onClick={() => setSize(size + 1)}
            disabled={isValidating}
          />
        )}
        {isReachingEnd && projects.length > 0 && (
          <p className="text-gray-500">{t('no_more')}</p>
        )}
      </div>
    </div>
  )
}

export const UserProjectList = ({
  transparent = false,
  showTitle = true,
  onLoadingChange,
}: {
  transparent?: boolean
  showTitle?: boolean
  onLoadingChange?: (loading: boolean) => void
}) => {
  const { data: user } = useCurrentUser()
  const limit = 8 // Reduced from 20 to 8 for faster initial load
  const t = useTranslations('projects')

  const { data, error, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite((pageIndex) => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (pageIndex * limit).toString(),
      })
      return `/api/me/projects?${params}`
    }, fetcher)

  const projects = data ? data.flat() : []
  const isReachingEnd = data && data[data.length - 1]?.length < limit

  // Show cards as soon as we have projects
  const hasProjects = projects.length > 0

  // Update loading state when data loading changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading && !data)
    }
  }, [isLoading, data, onLoadingChange])

  if (error) return <p className="text-red-500">{t('failed')}</p>

  // Show loading overlay when initially loading
  const showInitialLoading = isLoading && projects.length === 0

  if (!projects || projects.length === 0) {
    // Just show skeleton cards, no black box loading overlay
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="animate-pulse">
              <div className="bg-gray-700/30 rounded-lg h-48 mb-3 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-gray-600/0 via-gray-500/20 to-gray-600/0 -translate-x-full animate-[shimmer_1.5s_infinite]" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-600/40 rounded w-3/4"></div>
                <div className="h-3 bg-gray-600/30 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div
      className={cn(
        'rounded-2xl relative',
        !transparent &&
          'bg-gradient-to-br from-white via-white to-gray-50 border border-gray-200/50 p-5 shadow-xl shadow-gray-200/50 backdrop-blur-xl',
      )}
    >
      {showTitle && (
        <div className="mb-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <PaletteIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-2xl font-black text-gray-900">
              {t('my_creations.title')}
            </h2>
          </div>
          <p className="text-gray-500 ml-[44px]">
            {t('my_creations.subtitle')}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 min-[1800px]:grid-cols-5 gap-4">
        {projects.map((project) => {
          const isOwner = project.user_id === user?._id.toString()
          // If project has workspace_id and user can see it (via /api/me/projects), user is a workspace member
          const isWorkspaceMember = Boolean(project.workspace_id)
          return (
            <ProjectCard
              key={project._id.toString()}
              project={project}
              isOwner={isOwner}
              isWorkspaceMember={isWorkspaceMember}
              showActions
              canDelete={isOwner}
              mutate={mutate}
              useHref
              linkToNewTab={false}
              transparent={transparent}
            />
          )
        })}
      </div>

      <div className="flex justify-center mt-6 relative">
        {!isReachingEnd && (
          <LoadMoreBtn
            onClick={() => setSize(size + 1)}
            disabled={isValidating}
          />
        )}
        {isReachingEnd && projects.length > 0 && (
          <p className="text-gray-500">{t('no_more')}</p>
        )}
      </div>
    </div>
  )
}

const StatusBadge = ({ status }: { status: string }) => {
  const t = useTranslations('projects.status')

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return 'text-green-400 bg-green-500/20 border border-green-500/30'
      case 'in progress':
        return 'text-blue-400 bg-blue-500/20 border border-blue-500/30'
      case 'building':
        return 'text-amber-400 bg-amber-500/20 border border-amber-500/40'
      case 'draft':
        return 'text-amber-300/80 bg-amber-500/10 border border-amber-500/30'
      default:
        return 'text-amber-300/80 bg-amber-500/10 border border-amber-500/30'
    }
  }

  const getTranslatedStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live':
        return t('live')
      case 'in progress':
        return t('in_progress')
      case 'building':
        return 'Building...'
      case 'draft':
        return t('draft')
      default:
        return status
    }
  }

  return (
    <span
      className={cn(
        'px-3 py-1 rounded-full text-sm font-medium capitalize',
        getStatusColor(status),
      )}
    >
      {getTranslatedStatus(status)}
    </span>
  )
}

const DisabledProjectCardContent = ({ project }: { project: UserProject }) => {
  const t = useTranslations('project-list')

  return (
    <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/80 saturate-50">
      <div className="relative">
        <div className="w-full aspect-video rounded-lg flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200/50">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-gray-200/80 flex items-center justify-center">
              <LockIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="absolute top-3 right-3">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200/80 text-gray-600 backdrop-blur-sm">
            {t('private.badge')}
          </span>
        </div>
      </div>

      <h3 className="text-lg font-semibold mt-4 mb-1 text-gray-500">
        {t('private.title')}
      </h3>

      <div className="flex items-center gap-2 text-sm text-gray-400">
        {project.create_time && (
          <time>{format(new Date(project.create_time), 'MMM d, yyyy')}</time>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
          <span className="text-xs text-gray-500">?</span>
        </div>
        <span className="text-sm text-gray-400">
          {project.user?.name || ''}
        </span>
      </div>
    </div>
  )
}

const ProjectCardContent = ({
  project,
  isPrivate,
  isOwner,
  isShared = false,
  showActions = false,
  showAvatar = true,
  mutate,
  cardClassName,
  projectNameClassName,
  projectTimeClassName,
  transparent = false,
  showHosting = false,
  compact = false,
  canDelete = false,
  onDeleteSuccess,
}: {
  project: UserProject
  isPrivate: boolean
  isOwner: boolean
  isShared?: boolean
  showActions?: boolean
  showAvatar?: boolean
  mutate: SWRInfiniteKeyedMutator<any[]> | undefined
  cardClassName?: string
  projectNameClassName?: string
  projectTimeClassName?: string
  transparent?: boolean
  showHosting?: boolean
  compact?: boolean
  canDelete?: boolean
  onDeleteSuccess?: () => Promise<void> | void
}) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const t = useTranslations('project-list')
  const isSubscribed: 'subscribed' | 'unsubscribed' | '' = 'subscribed'

  const setIsAnyAlertDialogOpen = useGlobalStore(
    (state) => state.setIsAnyAlertDialogOpen,
  )

  useEffect(() => {
    setIsAnyAlertDialogOpen(showDeleteConfirm)
  }, [showDeleteConfirm, setIsAnyAlertDialogOpen])

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      const response = await fetch(`/api/me/project?projectId=${project._id}`, {
        method: 'DELETE',
      })

      if (response.ok) {
        // Call mutate if provided (for SWR infinite lists)
        if (mutate) {
          await mutate()
        }
        // Call onDeleteSuccess callback if provided (for custom refresh logic)
        if (onDeleteSuccess) {
          await onDeleteSuccess()
        }
        toast.success(t('toast.delete_success'))
      } else {
        // Handle specific error cases
        let errorMessage = t('toast.delete_error')
        try {
          const errorData = await response.json()
          errorMessage = errorData.message || errorData.error || errorMessage
        } catch {
          // Ignore JSON parse errors
        }

        if (response.status === 403) {
          errorMessage =
            t('toast.delete_permission_error') ||
            'You do not have permission to delete this project'
        } else if (response.status === 404) {
          errorMessage = t('toast.delete_not_found') || 'Project not found'
        }
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Delete project error:', error)
      toast.error(t('toast.delete_error'))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  return (
    <>
      <div
        className={cn(
          'group rounded-xl transition-all duration-200 relative bg-gradient-to-br from-white/5 via-white/5 to-white/[0.02] backdrop-blur-md hover:from-white/8 hover:via-white/8 hover:to-white/5 hover:scale-[1.02] overflow-hidden shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-black/30 flex flex-col w-full',
          cardClassName,
          {
            'h-[320px]': !compact,
          },
        )}
      >
        <div className="relative flex-shrink-0">
          <div className="w-full aspect-video flex items-center justify-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black relative border border-amber-500/20">
            {project.thumbnail ? (
              <>
                {/* Loading spinner */}
                <div className="absolute inset-0 bg-gradient-to-br from-black/90 to-gray-900/90 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-400/80 rounded-full animate-spin" />
                </div>
                <img
                  src={project.thumbnail}
                  alt={project.project_name}
                  className="w-full h-full object-cover transition-opacity"
                  onLoad={(e) => {
                    // Hide spinner when image loads
                    const spinner = e.currentTarget
                      .previousElementSibling as HTMLElement
                    if (spinner) spinner.style.display = 'none'
                  }}
                />
              </>
            ) : project.initial_coding_stage === 'ing' ||
              project.status === 'in progress' ? (
              // Building state - enhanced shimmer effect with gold theme
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden border border-amber-500/20">
                {/* Animated gradient background with gold accents */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-amber-400/10 to-amber-500/5 animate-[shimmer_2s_ease-in-out_infinite]" />
                {/* Grid pattern overlay with gold */}
                <div
                  className="absolute inset-0 opacity-10"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(251, 191, 36, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(251, 191, 36, 0.1) 1px, transparent 1px)',
                    backgroundSize: '20px 20px',
                  }}
                />
                {/* Center icon with pulse effect - gold theme */}
                <div className="relative z-10 flex items-center justify-center">
                  <Layout className="w-12 h-12 text-amber-400/90 animate-pulse" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-amber-500/30 animate-ping" />
                  </div>
                </div>
                {/* Shimmer overlay with gold */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/10 to-transparent -translate-x-full animate-[shimmer_2s_ease-in-out_infinite]" />
              </div>
            ) : (
              // Completed project without thumbnail - show Heyboss logo with gold theme
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden border border-amber-500/20">
                <div className="relative z-10 flex items-center justify-center p-8">
                  <div className="relative w-24 h-24 flex items-center justify-center">
                    <Image
                      src="/v2/lightlogo2.png"
                      alt="Heyboss AI"
                      width={96}
                      height={96}
                      className="w-full h-full object-contain opacity-90"
                    />
                    {/* Gold glow effect */}
                    <div className="absolute inset-0 bg-amber-400/30 blur-xl rounded-full" />
                  </div>
                </div>
                {/* Subtle background pattern with gold */}
                <div
                  className="absolute inset-0 opacity-5"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle at 2px 2px, rgba(251, 191, 36, 0.2) 1px, transparent 0)',
                    backgroundSize: '24px 24px',
                  }}
                />
              </div>
            )}
          </div>

          {isPrivate && (
            <div className="absolute top-3 left-3">
              <div className="w-8 h-8 rounded-lg bg-white/80 backdrop-blur flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04]">
                <LockIcon className="w-4 h-4 text-gray-700" />
              </div>
            </div>
          )}

          {/* Shared badge - show when project is shared with user */}
          {isShared && (
            <div className="absolute top-3 left-3">
              <div className="h-6 px-2 rounded-md bg-blue-500/90 backdrop-blur flex items-center gap-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
                <Users className="w-3 h-3 text-white" />
                <span className="text-[10px] font-medium text-white">
                  Shared
                </span>
              </div>
            </div>
          )}

          {/* Status badge - always visible */}
          <div className="absolute top-3 right-12">
            <StatusBadge
              status={
                project.initial_coding_stage === 'ing' ||
                project.status === 'in progress'
                  ? 'building'
                  : project.status || 'Draft'
              }
            />
          </div>

          {/* Delete button - visible based on canDelete permission */}
          {showActions && canDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowDeleteConfirm(true)
              }}
              className="absolute top-3 right-3 w-7 h-7 rounded-lg bg-white/80 backdrop-blur hover:bg-white/90 border border-white/20 hover:border-white/40 flex items-center justify-center transition-all shadow-[0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04]"
              title="Delete project"
            >
              <Trash2 className="w-3.5 h-3.5 text-gray-700" />
            </button>
          )}
        </div>

        <div
          className={cn(
            'select-none relative flex-1 flex flex-col justify-center',
            compact ? 'px-4 py-2' : 'px-6 py-4 min-h-[80px]',
          )}
        >
          <h3
            className={cn(
              'text-base mb-1 line-clamp-2 text-white leading-tight',
              projectNameClassName,
              {
                'font-semibold': !compact,
              },
            )}
          >
            {project.project_name}
          </h3>

          {showAvatar && (
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                {project.user?.avatar ? (
                  <img
                    src={project.user.avatar}
                    alt={''}
                    width={24}
                    height={24}
                    className="rounded-full"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-xs text-gray-500">
                      {project.user?.name?.charAt(0) || '?'}
                    </span>
                  </div>
                )}
                <span className="text-sm text-gray-600">
                  {project.user?.name || ''}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                {project.create_time && (
                  <time
                    className={cn(
                      'text-[12px] text-gray-400',
                      projectTimeClassName,
                    )}
                  >
                    {format(new Date(project.create_time), 'MMM d, yyyy')}
                  </time>
                )}
              </div>
            </div>
          )}
          {/* {isOwner && showHosting && (
            <div className="absolute bottom-3 right-0">
              {plan === 'free' && (
                <Button
                  variant="outline_2"
                  className="bg-[#27394b] rounded-full text-xs text-[#15F1FD] !px-2 !py-0 font-medium h-6"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    pushModal('PricingModal', {
                      projectId: project._id.toString(),
                    })
                  }}
                >
                  {t('subscribe_to_host')}
                </Button>
              )}
              {plan === 'basic' && (
                <Button
                  variant="outline_2"
                  className="bg-[#353843] rounded-full text-xs text-white !px-2 !py-0 font-medium h-6"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <IconBasicHosting className="!w-3 !h-3" />
                  {t('basic_hosting')}
                </Button>
              )}
              {plan === 'premium' && (
                <Button
                  variant="outline_2"
                  className="bg-[#353843] rounded-full text-xs text-white !px-2 !py-0 font-medium h-6"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <IconPremiumHosting className="!w-3 !h-3" />
                  {t('premium_hosting')}
                </Button>
              )}
            </div>
          )} */}
        </div>
      </div>

      <AlertDialog open={showDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_confirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_confirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="text-muted-foreground"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowDeleteConfirm(false)
              }}
              disabled={isDeleting}
            >
              {t('delete_confirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDelete()
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  <span>{t('actions.deleting')}</span>
                </div>
              ) : (
                t('delete_confirm.confirm')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export const ProjectCard = ({
  project,
  isOwner,
  isShared = false,
  showActions = false,
  mutate,
  useHref = true,
  showAvatar = true,
  cardClassName,
  projectNameClassName,
  projectTimeClassName,
  linkToNewTab = true,
  onClickLink,
  transparent = false,
  showHosting = false,
  compact = false,
  canDelete = false,
  onDeleteSuccess,
  isWorkspaceMember = false,
}: {
  project: UserProject
  isOwner: boolean
  isShared?: boolean
  showActions?: boolean
  mutate?: SWRInfiniteKeyedMutator<any[]>
  useHref?: boolean
  showAvatar?: boolean
  cardClassName?: string
  projectNameClassName?: string
  projectTimeClassName?: string
  linkToNewTab?: boolean
  onClickLink?: (e: React.MouseEvent) => void
  transparent?: boolean
  showHosting?: boolean
  compact?: boolean
  canDelete?: boolean
  onDeleteSuccess?: () => Promise<void> | void
  isWorkspaceMember?: boolean // If true, user is a member of the workspace containing this project
}) => {
  const isPrivate = project.visibility === 'private'
  // Allow access if: owner OR workspace member (for workspace projects)
  // For workspace projects, if user is a member, they should be able to access private projects
  const hasAccess = isOwner || (isWorkspaceMember && project.workspace_id)
  const isDisabled = isPrivate && !hasAccess

  if (isDisabled) {
    return <DisabledProjectCardContent project={project} />
  }

  const content = (
    <ProjectCardContent
      project={project}
      isPrivate={isPrivate}
      isOwner={isOwner}
      isShared={isShared}
      showActions={showActions}
      mutate={mutate}
      cardClassName={cardClassName}
      projectNameClassName={projectNameClassName}
      projectTimeClassName={projectTimeClassName}
      showAvatar={showAvatar}
      transparent={transparent}
      showHosting={showHosting}
      compact={compact}
      canDelete={canDelete}
      onDeleteSuccess={onDeleteSuccess}
    />
  )

  if (useHref) {
    return (
      <Link
        href={`/project?id=${project._id}`}
        target={linkToNewTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        onClick={onClickLink}
      >
        {content}
      </Link>
    )
  }

  return content
}
